import { HttpErrorResponse } from "@angular/common/http";
import { Injectable, signal } from "@angular/core";
import { patchState, signalStore, withState } from "@ngrx/signals";
import moment from "moment";
import { EMPTY, Observable, catchError, defer, finalize, firstValueFrom, from, of, switchMap, tap } from "rxjs";
import { map } from "rxjs/operators";
import { ListResponse } from "../models/interfaces/list-response.interface";
import type { DispatchGuideOrderNumberType } from "../models/ost/documents/dispatch-guide.model";
import { F11EstadoCreacionEnum } from "../models/ost/enums/f11-estado-creacion.enum";
import { OstEstadoEnum } from "../models/ost/enums/ost-estado.enum";
import { OstPaisEnum } from "../models/ost/enums/ost-pais.enum";
import { OstEvent, getOstResponseChangesFromEvent } from "../models/ost/events/ost-sse-event.model";
import { MailLabelLabelDto, MailLabelListRs } from "../models/ost/mail-label-list-rs.model";
import { OstHistoricalResponse } from "../models/ost/ost-historical-response.model";
import type { OstMailLabelsResponse } from "../models/ost/ost-mail-labels-response.model";
import { OstResponse } from "../models/ost/ost-response.model";
import { OstUpdate } from "../models/ost/ost-update.model";
import { ReqOST } from "../models/ost/req-ost.model";
import { getLastMailLabel } from "../models/ost/table/label-cell.model";
import { WarrantiesTypeEnum } from "../models/warranties/warranties-type.enum";
import { IndexDbDocumentService } from "../services/index-db-document.service";
import { OstService } from "../services/ost.service";
import { RequestStatusHttpError, withRequestStatus } from "./features/with-request-status.feature";
import { withStorage } from "./features/with-storage.feature";
import { UserStore } from "./user.store";

interface SerializedOstState {
  listOst: ListResponse<OstResponse>;
  listLabels: ListResponse<MailLabelLabelDto>;
  ostFromLabels: Record<string, OstResponse>;
  selectedOstFromTable: OstResponse;
  historical: [number, Array<OstHistoricalResponse>][];
  latestOstNumHistorical: number;
  queriesParams: Record<string, string>;
}

export type OstState = {
  listOst: ListResponse<OstResponse>;
  listLabels: ListResponse<MailLabelLabelDto>;
  ostFromLabels: Record<string, OstResponse>;
  selectedOstFromTable: OstResponse;
  historical: Map<number, Array<OstHistoricalResponse>>;
  queriesParams: Record<string, string>;
};

const initialState: OstState = {
  listOst: {
    data: [],
    page: 0,
    pageSize: 0,
    total: 0,
    totalPages: 0,
  },
  listLabels: {
    data: [],
    page: 0,
    pageSize: 0,
    total: 0,
    totalPages: 0,
  },
  ostFromLabels: {},
  selectedOstFromTable: new OstResponse(),
  historical: new Map<number, Array<OstHistoricalResponse>>(),
  queriesParams: {
    dni: "",
    ostNum: "",
    f11Num: "",
  },
};

@Injectable({ providedIn: "root" })
export class OstStore extends signalStore(
  { protectedState: false },
  withState<OstState>(initialState),
  withRequestStatus(),
  withStorage("ost", sessionStorage, {
    saveExcludeKeys: ["isLoading", "hasError", "httpError"],
    loadExcludeKeys: ["isLoading", "hasError", "httpError"],
    serialize: (state: OstState) => {
      const historicalEntries = Array.from(state.historical.entries()).map(([ostNum, valueMap]: [number, Array<OstHistoricalResponse>]): [number, Array<OstHistoricalResponse>] => {
        return [ostNum, valueMap];
      });
      return JSON.stringify({
        ...state,
        historical: historicalEntries,
      });
    },
    deserialize: (serializedState: string) => {
      const parsed = JSON.parse(serializedState) as SerializedOstState;
      const historical = new Map<number, Array<OstHistoricalResponse>>();

      if (parsed.historical.length > 0) {
        parsed.historical.forEach(([key, valueArray]: [number, Array<OstHistoricalResponse>]) => {
          historical.set(key, valueArray);
        });
      }

      return {
        ...parsed,
        historical,
        isLoading: false,
        hasError: false,
        httpStatusCode: 0,
        httpError: null,
      };
    },
  })
) {
  private readonly errorHandled$ = signal(false);

  constructor(
    private readonly ostService: OstService,
    private readonly indexDbDocumentService: IndexDbDocumentService,
    private readonly userStore: UserStore
  ) {
    super();
  }

  private autoResetError(): void {
    if (this.hasError()) {
      setTimeout(() => {
        patchState(this, { hasError: false, httpStatusCode: 0, httpError: null });
        this.errorHandled$.set(true);
      }, 0);
    }
  }

  private serializeErrorPayload(error: unknown): unknown {
    try {
      structuredClone(error);
      return error;
    } catch {
      return error instanceof Error
        ? {
            name: error.name,
            message: error.message,
            stack: error.stack,
          }
        : String(error);
    }
  }

  private serializeHttpError(err: unknown): RequestStatusHttpError {
    if (err instanceof HttpErrorResponse) {
      return {
        status: err.status,
        statusText: err.statusText,
        url: err.url,
        message: err.message,
        error: this.serializeErrorPayload(err.error),
      };
    }

    return {
      status: 0,
      statusText: "",
      url: null,
      message: err instanceof Error ? err.message : String(err),
      error: this.serializeErrorPayload(err),
    };
  }

  loadData(
    start: moment.Moment,
    end: moment.Moment,
    ost_pais: OstPaisEnum | null,
    page: number,
    pageSize: number,
    ost_estado?: OstEstadoEnum,
    f11_estado_creacion?: string,
    warranty_type_code?: string,
    dni?: string
  ): Observable<ListResponse<OstResponse>> {
    const effectivePais = ost_pais ?? OstPaisEnum.CL;

    if (dni) {
      patchState(this, (state: OstState) => ({
        ...state,
        queriesParams: { ...state.queriesParams, dni },
        isLoading: true,
      }));
    } else patchState(this, { isLoading: true });

    const fechaCreacionDesde = start.format("YYYY-MM-DD");
    const fechaCreacionHasta = end.format("YYYY-MM-DD");

    const warrantyTypeCodeFiltered: WarrantiesTypeEnum | undefined =
      warranty_type_code && warranty_type_code.toUpperCase() !== "TODOS" ? (warranty_type_code as WarrantiesTypeEnum) : undefined;

    const ostEstadoFiltered: OstEstadoEnum | undefined = ost_estado && ost_estado.toUpperCase() !== "TODOS" ? (ost_estado.toUpperCase() as OstEstadoEnum) : undefined;

    const f11EstadoCreacionFiltered: boolean | undefined =
      f11_estado_creacion && f11_estado_creacion.toUpperCase() !== "TODOS"
        ? f11_estado_creacion.toUpperCase() !== F11EstadoCreacionEnum.EN_PROCESO.toString().toUpperCase()
        : undefined;

    return this.ostService
      .getListOst(
        fechaCreacionDesde,
        fechaCreacionHasta,
        effectivePais,
        page,
        pageSize,
        this.userStore.user().storeCode,
        ostEstadoFiltered,
        f11EstadoCreacionFiltered,
        warrantyTypeCodeFiltered,
        dni
      )
      .pipe(
        tap({
          next: (response: ListResponse<OstResponse>): void => {
            patchState(this, { listOst: response, hasError: false });
          },
          error: (): void => {
            patchState(this, { hasError: true });
          },
        }),
        catchError(() => EMPTY),
        finalize((): void => {
          patchState(this, { isLoading: false });
        })
      );
  }

  fetchOstWithMailLabels(page: number, pageSize: number, start: moment.Moment, end: moment.Moment, f11_estado_creacion?: string, trackingNumber?: string, ostNum?: string): void {
    patchState(this, { isLoading: true });

    const labelGenerationStart = start.format("YYYY-MM-DD");
    const labelGenerationEnd = end.format("YYYY-MM-DD");
    const userStoreId = this.userStore.user().storeCode;
    const f11EstadoCreacionFiltered =
      f11_estado_creacion && f11_estado_creacion.toUpperCase() !== "TODOS"
        ? f11_estado_creacion.toUpperCase() !== F11EstadoCreacionEnum.EN_PROCESO.toString().toUpperCase()
        : undefined;

    this.ostService.getMailLabels(userStoreId, page, pageSize, labelGenerationStart, labelGenerationEnd, f11EstadoCreacionFiltered, trackingNumber, ostNum).subscribe({
      next: (response: MailLabelListRs): void => {
        patchState(this, {
          listLabels: response.mailLabels,
          ostFromLabels: response.ostsByNum,
          isLoading: false,
          hasError: false,
        });
      },
      error: (): void => {
        patchState(this, { isLoading: false, hasError: true });
      },
    });
  }

  async createOst(req: ReqOST): Promise<OstResponse> {
    patchState(this, { isLoading: true, hasError: false, httpStatusCode: 0, httpError: null });

    try {
      const response = await firstValueFrom(this.ostService.createOst(req));
      patchState(this, { isLoading: false, hasError: false, httpStatusCode: 0, httpError: null });
      return response;
    } catch (err: unknown) {
      const hasHttpError = err instanceof HttpErrorResponse;
      patchState(this, {
        isLoading: false,
        hasError: true,
        httpStatusCode: hasHttpError ? err.status : 0,
        httpError: this.serializeHttpError(err),
      });
      this.errorHandled$.set(false);
      this.autoResetError();

      return {} as OstResponse;
    }
  }

  getOstByNum(ostNum: number, searchType: "OST" | "F11" | "asset", setSelectedOst?: boolean): Observable<void> {
    return defer(() => {
      patchState(this, { isLoading: true });

      return this.ostService.getOstByNum(ostNum, searchType, this.userStore.user().storeCode).pipe(
        tap({
          next: (ostResponse: OstResponse) => {
            if (setSelectedOst) {
              this.setSelectedOstFromTable(ostResponse);
              patchState(this, { hasError: false });
            }
          },
          error: (err: unknown) => {
            patchState(this, {
              isLoading: false,
              hasError: true,
              httpStatusCode: err instanceof HttpErrorResponse ? err.status : 0,
            });
            this.errorHandled$.set(false);
            this.autoResetError();
          },
        }),
        finalize(() => {
          patchState(this, { isLoading: false });
        }),
        map(() => void 0)
      );
    });
  }

  getListOstByDni(dni: string, ost_pais: OstPaisEnum | undefined, page: number, pageSize: number): void {
    const pais = ost_pais ?? OstPaisEnum.CL;
    patchState(this, (state: OstState) => ({
      ...state,
      queriesParams: { ...state.queriesParams, dni },
      isLoading: true,
    }));

    this.ostService.getListOstByDni(dni, pais, page, pageSize).subscribe({
      next: (listOst: ListResponse<OstResponse>) => {
        patchState(this, { listOst, isLoading: false, hasError: false });
      },
      error: (err: unknown) => {
        console.error("Error fetching OST list by DNI:", err);
        patchState(this, { isLoading: false, hasError: true });
      },
    });
  }

  getDispatchGuide(ost: OstResponse): Observable<void> {
    return defer(() => {
      patchState(this, { isLoading: true });

      const currentTrackingNumber = this.getLatestTrackingNumber(ost.mailLabels);
      const trackingNumber$ = currentTrackingNumber
        ? of(currentTrackingNumber)
        : this.ostService
            .getMailLabelsByOstNum(String(ost.ostNum), this.userStore.user().storeCode)
            .pipe(map((response) => this.getLatestTrackingNumber(response.mailLabels.data)));

      return trackingNumber$.pipe(
        switchMap((trackingNumber) => {
          const orderNumberType: DispatchGuideOrderNumberType = trackingNumber ? "trackingNumber" : "OST";
          const identifier = trackingNumber ?? String(ost.ostNum);
          const documentKey = `guide:${orderNumberType}:${identifier}`;

          return from(this.indexDbDocumentService.getDispatchGuide(documentKey, identifier, orderNumberType));
        }),
        catchError((err: unknown) => {
          const hasHttpError = err instanceof HttpErrorResponse;
          patchState(this, {
            isLoading: false,
            hasError: true,
            httpStatusCode: hasHttpError ? err.status : 0,
          });
          this.errorHandled$.set(false);
          this.autoResetError();
          throw err;
        }),
        finalize(() => {
          patchState(this, { isLoading: false });
        }),
        map(() => void 0)
      );
    });
  }

  private getLatestTrackingNumber(mailLabels?: OstMailLabelsResponse[]): string | undefined {
    const labelsWithTrackingNumber = mailLabels?.filter((mailLabel) => mailLabel.typeLabel === "SHIPPING" && Boolean(mailLabel.trackingNumber?.trim()));
    return getLastMailLabel(labelsWithTrackingNumber)?.trackingNumber.trim() || undefined;
  }

  getHistoricalOst(ostNum: number): void {
    patchState(this, { isLoading: true });
    const existingOst = this.historical().get(ostNum);

    if (existingOst) {
      patchState(this, { isLoading: false });
      return;
    }

    this.ostService.getOstHistorical(ostNum).subscribe({
      next: (response: Array<OstHistoricalResponse>): void => {
        patchState(this, (state: OstState) => {
          const historical = new Map(state.historical);
          historical.set(ostNum, response);

          return { ...state, historical, isLoading: false };
        });
      },
      error: (): void => {
        patchState(this, { isLoading: false, hasError: true });
      },
    });
  }

  setSelectedOstFromTable(selectedOst: OstResponse): void {
    patchState(this, { selectedOstFromTable: selectedOst });
  }

  updateOstListFromEvent(event: OstEvent): void {
    const rawChanges = getOstResponseChangesFromEvent(event);
    const changes = Object.fromEntries(Object.entries(rawChanges).filter(([, value]) => value !== undefined)) as Partial<OstResponse>;

    if (Object.keys(changes).length === 0 || event.ostNum == null) {
      return;
    }

    this.updateOstInList(event.ostNum.toString(), changes);
  }

  updateOstInList(ostNum: string, changes: Partial<OstResponse>): void {
    patchState(this, (state: OstState): Partial<OstState> => {
      let found = false;

      const data = state.listOst.data.map((ost: OstResponse): OstResponse => {
        const isMatch = ost.ostNum?.toString() === ostNum;

        if (!isMatch) {
          return ost;
        }

        found = true;

        return {
          ...ost,
          ...changes,
        };
      });

      if (!found) {
        return {};
      }

      const selectedOstFromTable =
        state.selectedOstFromTable.ostNum?.toString() === ostNum
          ? {
              ...state.selectedOstFromTable,
              ...changes,
            }
          : state.selectedOstFromTable;

      return {
        listOst: {
          ...state.listOst,
          data,
        },
        selectedOstFromTable,
      };
    });
  }

  addOstToOstList(ost: OstResponse): void {
    patchState(this, (state: OstState) => {
      const alreadyExists = state.listOst.data.some((o: OstResponse) => o.ostNum === ost.ostNum);
      if (alreadyExists) {
        return state;
      }

      const updatedList = [ost, ...state.listOst.data];
      const newTotal = state.listOst.total + 1;
      const newTotalPages = state.listOst.pageSize > 0 ? Math.ceil(newTotal / state.listOst.pageSize) : state.listOst.totalPages;

      return {
        ...state,
        listOst: {
          ...state.listOst,
          data: updatedList,
          total: newTotal,
          totalPages: newTotalPages,
        },
      };
    });
  }

  updateQueriesParams(updatedParams: Partial<OstState["queriesParams"]>): void {
    const sanitizedParams = Object.fromEntries(Object.entries(updatedParams).filter(([, value]: [string, string | undefined]) => value !== undefined)) as Record<string, string>;

    patchState(this, (state: OstState) => ({
      ...state,
      queriesParams: {
        ...state.queriesParams,
        ...sanitizedParams,
      },
    }));
  }

  updateOstByNum(ostNum: number, reqOst: OstUpdate, showLoading?: boolean): Observable<void> {
    patchState(this, { isLoading: showLoading === true });
    return this.ostService.updateOstByNum(ostNum, reqOst).pipe(
      tap({
        next: () => patchState(this, { isLoading: false, hasError: false }),
        error: (err: unknown) => {
          const hasHttpError = err instanceof HttpErrorResponse;
          patchState(this, {
            isLoading: false,
            hasError: true,
            httpStatusCode: hasHttpError ? err.status : 0,
          });
          this.errorHandled$.set(false);
          this.autoResetError();
        },
      }),
      finalize(() => {
        patchState(this, { isLoading: false });
      }),
      map(() => void 0)
    );
  }

  /**
   * Valida si la boleta tiene una boleta de cambio asociada
   */
  checkHasBoletaCambio(invNumBoleta: string): Observable<boolean> {
    return this.ostService.checkHasBoletaCambio(invNumBoleta).pipe(
      catchError((err: unknown) => {
        const hasHttpError = err instanceof HttpErrorResponse;
        patchState(this, {
          hasError: true,
          httpStatusCode: hasHttpError ? err.status : 0,
        });
        this.errorHandled$.set(false);
        this.autoResetError();
        throw err;
      })
    );
  }
}
